"use client";

import { AuthShell, AuthShellHeaderButton } from "./AuthShell";
import { AuthUI } from "./auth-ui";
import { SignUpShowcase } from "./interfaces/SignUpShowcase";
import { AUTH_VIEWS, isAuthView, ViewType } from "./types";
import {
  useParseErrorsFromHash,
  useParseImplicitFlowAccessToken,
  useSetPKCECookie,
} from "./useAuthCallback";
import type { PasswordLoginMode } from "@/components/auth-ui/types";
import { cn } from "@/lib/utils";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import * as React from "react";

export const useNavigateAuthState = () => {
  const searchParams = useSearchParams();
  const rawView = searchParams.get("view");
  const view = isAuthView(rawView) ? rawView : AUTH_VIEWS.SIGN_IN;
  const router = useRouter();
  const setView = (view: ViewType) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", view);
    params.delete("intent");

    // Native history push keeps the view switch fully client-side (no RSC
    // round trip), so the sign-up layout and background flip instantly.
    window.history.pushState(null, "", `/auth?${params.toString()}`);
  };
  return { view, setView, router, searchParams };
};

export function AuthPage({
  passwordLoginMode = "turnstile",
}: {
  passwordLoginMode?: PasswordLoginMode;
}) {
  const { view, setView, searchParams } = useNavigateAuthState();
  useSetPKCECookie();
  useParseErrorsFromHash();
  const { showSpinner, isProcessingToken, handleCancel } =
    useParseImplicitFlowAccessToken();

  if (showSpinner) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div
            aria-label="Signing you in"
            className="h-14 w-14 animate-spin rounded-full border-4 border-neutral-200 border-t-neutral-900"
          />
          {isProcessingToken && (
            <>
              <p className="text-neutral-600 text-sm">Signing you in...</p>
              <button
                onClick={handleCancel}
                className="text-neutral-400 text-xs hover:text-neutral-600 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const isSignUpView = view === AUTH_VIEWS.SIGN_UP;
  const hideSignUpAction =
    view === AUTH_VIEWS.MAGIC_LINK && !!searchParams.get("access_key");

  const headerAction = hideSignUpAction ? undefined : isSignUpView ? (
    <AuthShellHeaderButton isCream onClick={() => setView(AUTH_VIEWS.SIGN_IN)}>
      Log in
    </AuthShellHeaderButton>
  ) : (
    <AuthShellHeaderButton onClick={() => setView(AUTH_VIEWS.SIGN_UP)}>
      Sign up
    </AuthShellHeaderButton>
  );

  const authColumn = (
    <div className="flex w-full items-center justify-center px-6 py-10">
      <div className="w-full max-w-[320px]">
        <AuthUI
          view={view}
          setView={setView}
          passwordLoginMode={passwordLoginMode}
        />
      </div>
    </div>
  );

  return (
    <AuthShell
      isCream={isSignUpView}
      headerAction={headerAction}
      showSignupConsent={isSignUpView}
    >
      <div className="flex w-full">
        <div className="flex flex-1 items-center justify-center">
          {authColumn}
        </div>
        {/* Kept mounted (hidden) on other views so the photo is already
            loaded when the user switches to sign-up. */}
        <div
          className={cn(
            "pr-6",
            isSignUpView ? "hidden flex-1 lg:block" : "hidden"
          )}
        >
          <SignUpShowcase />
        </div>
      </div>
    </AuthShell>
  );
}
